/* eslint-disable complexity */
import React, { FocusEvent, useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { LoaderSpinner } from 'carbon-react/lib/components/loader-spinner';
import Form from 'carbon-react/lib/components/form';
import Button from 'carbon-react/lib/components/button';
import Box from 'carbon-react/lib/components/box';

import { toastError, toastSuccess } from '@futrli/ui-components';
import { Logger, validateEmailAddress } from '@futrli/shared-services';

import { FORM_FIELDS } from '../../../../../../../auth/src/core/constants';
import { usePredictionsInfoContext } from '../../../context/predictions-info-context';
import { AdvisorAuthForm } from './advisor-auth-form';
import { AdvisorScenarioList } from './advisor-scenario-list';
import { fetchAdvisorScenarios, importAdvisorScenarios, loginToAdvisor } from '../../../../../core/api/advisor';
import { AuthFormData, AuthTouchedValues, ImportedScenario } from './advisor';
import { ImportAssumptionsAdvisorContainer } from './import-assumptions-advisor-container';

interface Props {
  isOpen: boolean;
  handleClose: () => void;
}

export const ImportAssumptionsFromAdvisor = ({ isOpen, handleClose }: Props) => {
  const intl = useIntl();

  const { workspaceId: tempWorkspaceId, organisationId: tempOrganisationId, forecastId: tempForecastId } = useParams();
  const workspaceId = tempWorkspaceId || '';
  const organisationId = tempOrganisationId || '';
  const forecastId = tempForecastId || '';
  const { fetchForecastReport } = usePredictionsInfoContext();
  const [errors, setErrors] = useState<Record<string, string>>({ username: '', password: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [scenarios, setScenarios] = useState<ImportedScenario[]>();
  const [authToken, setAuthToken] = useState<string>();
  const [advisorOrgId, setAdvisorOrgId] = useState<string>();
  const [selectedScenarios, setSelectedScenarios] = useState<Record<string, boolean>>();

  const [touchedValues, setTouchedValues] = useState<AuthTouchedValues>({
    username: false,
    password: false
  });

  const [formValues, setFormValues] = useState<AuthFormData>({
    username: '',
    password: '',
    region: ''
  });

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      const newFormValues = {
        ...formValues,
        [name]: value
      };
      setFormValues(newFormValues);

      const isEmailValid = validateEmailAddress(newFormValues.username);
      const isPasswordValid = newFormValues.password.length > 0;
      setErrors({
        [FORM_FIELDS.password]: isPasswordValid
          ? ''
          : intl.formatMessage({
              id: 'validation.invalid_password_description',
              defaultMessage:
                'Sorry, your password must be at least 9 or more characters and contain a capital letter, number and a symbol.'
            }),
        [FORM_FIELDS.username]: isEmailValid
          ? ''
          : intl.formatMessage({
              id: 'validation.invalid_email',
              defaultMessage: 'Please make sure you have entered a valid email address.'
            })
      });
      if (!touchedValues[name]) {
        setTouchedValues({
          ...touchedValues,
          [name]: true
        });
      }
    },
    [formValues, intl, touchedValues]
  );

  const handleBlur = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      const { name } = e.target;
      if (!touchedValues[name]) {
        setTouchedValues({
          ...touchedValues,
          [name]: true
        });
      }
    },
    [touchedValues]
  );

  const handleAuthClick = useCallback(async () => {
    try {
      setIsLoading(true);
      const authRequestPayload = {
        region: formValues.region,
        email: formValues.username,
        password: formValues.password
      };

      const advisorAuthToken = await loginToAdvisor(workspaceId, organisationId, forecastId, authRequestPayload);
      const fetchedScenarios = await fetchAdvisorScenarios(workspaceId, organisationId, forecastId, {
        region: formValues.region,
        advisor_token: advisorAuthToken.advisor_token
      });

      setAuthToken(advisorAuthToken.advisor_token);
      setAdvisorOrgId(fetchedScenarios.advisor_organisation_id);
      setScenarios(fetchedScenarios.scenarios);
      const selectedScenariosInit = fetchedScenarios.scenarios.reduce(
        (acc: Record<string, boolean>, curr: ImportedScenario) => {
          acc[curr.id] = false;
          return acc;
        },
        {}
      );
      setSelectedScenarios(selectedScenariosInit);
    } catch (e) {
      Logger.error(e as string);
      toastError();
    } finally {
      setIsLoading(false);
    }
  }, [forecastId, formValues.password, formValues.region, formValues.username, organisationId, workspaceId]);

  const handleImportAssumptionsClick = useCallback(async () => {
    if (!selectedScenarios || !authToken || !advisorOrgId) {
      return;
    }

    try {
      setIsLoading(true);
      const importInfo = await importAdvisorScenarios(workspaceId, organisationId, forecastId, {
        region: formValues.region,
        advisor_token: authToken,
        advisor_organisation_id: advisorOrgId,
        scenarios: Object.keys(selectedScenarios).filter(k => selectedScenarios[k])
      });

      toastSuccess(
        <FormattedMessage
          id="predict.import_assumptions_success"
          defaultMessage="{assumptionNum, plural, one {1 assumption is} other {{assumptionNum} assumptions are}} imported successfully"
          values={{ assumptionNum: importInfo.data.total_imported_assumptions }}
        />
      );

      fetchForecastReport?.();
      handleClose();
    } catch (e) {
      Logger.error(e as string);
      toastError();
    } finally {
      setIsLoading(false);
    }
  }, [
    advisorOrgId,
    authToken,
    fetchForecastReport,
    forecastId,
    formValues.region,
    handleClose,
    organisationId,
    selectedScenarios,
    workspaceId
  ]);

  const handleScenarioClick = useCallback(
    (clickedScenario: ImportedScenario) => {
      if (!selectedScenarios) {
        return;
      }

      setSelectedScenarios({ ...selectedScenarios, [clickedScenario.id]: !selectedScenarios[clickedScenario.id] });
    },
    [selectedScenarios]
  );

  const saveDisabled =
    isLoading ||
    !formValues.username ||
    !formValues.password ||
    !formValues.region ||
    !!errors.username ||
    !!errors.password;

  const noScenarioSelected =
    !selectedScenarios ||
    Object.keys(selectedScenarios).filter(k => !selectedScenarios[k]).length === Object.keys(selectedScenarios).length;

  const importDisabled = isLoading || noScenarioSelected || !authToken || !advisorOrgId;

  return (
    <ImportAssumptionsAdvisorContainer isOpen={isOpen} onClose={handleClose}>
      <Form
        stickyFooter
        leftSideButtons={
          <Button onClick={handleClose}>
            <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
          </Button>
        }
        saveButton={
          scenarios ? (
            <Button disabled={importDisabled} onClick={handleImportAssumptionsClick} buttonType="primary">
              <FormattedMessage id="common.import" defaultMessage="Import" />
            </Button>
          ) : (
            <Button disabled={saveDisabled} onClick={handleAuthClick} buttonType="primary">
              <FormattedMessage id="common.log_in" defaultMessage="Log in" />
            </Button>
          )
        }
        onSubmit={e => e.preventDefault()}
      >
        {isLoading ? (
          <Box p={4}>
            <LoaderSpinner showSpinnerLabel={false} />
          </Box>
        ) : authToken ? (
          <AdvisorScenarioList
            scenarios={scenarios}
            selectedScenarios={selectedScenarios}
            handleScenarioClick={handleScenarioClick}
          />
        ) : (
          <AdvisorAuthForm
            formValues={formValues}
            touchedValues={touchedValues}
            errors={errors}
            handleInputChange={handleInputChange}
            handleBlur={handleBlur}
          />
        )}
      </Form>
    </ImportAssumptionsAdvisorContainer>
  );
};
